<script context="module">
  import { getApolloClient, gql } from '$lib/apollo';
  export const TEAM_MEMBER_CARD_FRAGMENT = gql`
    fragment TeamMemberCardFragment on API_GroupTeamMember {
      id
      state
      position
      percentageSplit
      account {
        id
        slug
        name
        avatar {
          original {
            url
          }
        }
        gamesOffered(first: 1) {
          totalCount
          edges {
            node {
              id
              localizedShortTitles {
                en
              }
              localizedTitles {
                en
              }
              logoMain {
                original {
                  url
                }
              }
            }
          }
        }
      }
    }
  `;
</script>

<script lang="ts">
  import { GroupTeamMemberStateEnum, type Api_GroupTeamMember } from '$lib/types/api/graphql';

  import { createEventDispatcher } from 'svelte';
  import { captureException } from '@sentry/sveltekit';
  import { Toasts } from '$lib/stores';
  import { extractNodes, lazyComponent } from '$lib/utils';

  import Suspense from '$components/Suspense.svelte';
  import Image from '$mds/Image';
  import Dropdown, { DropdownMenuDivider, DropdownMenuItem } from '$mds/Dropdown';
  import IconButton from '$mds/Buttons/IconButton';
  import LoadingSpinner from '$mds/LoadingSpinner';

  import IconWrapper from '$mds/IconWrapper';
  import {
    faEllipsis,
    faGripDotsVertical,
    faPen,
    faTrash,
    faTriangleExclamation
  } from '@fortawesome/pro-solid-svg-icons';
  import Tag from '$mds/Tag';
  import { scale } from 'svelte/transition';

  const AddTeamMemberLazy = lazyComponent(
    () => import('$routes/(nav)/@[slug]/settings/team-members/_components/AddTeamMember.svelte')
  );

  const dispatch = createEventDispatcher();

  export let member: Api_GroupTeamMember;
  export let isOwner = false;
  export let isEditable = false;
  export let isSortable = false;
  export let isActive = false;
  export let link = false;

  $: game = extractNodes(member.account.gamesOffered)[0];

  let isUpdateTeamMemberOpen = false;

  let loading = false;
  async function handleRemoveTeamMember() {
    try {
      loading = true;
      const { data } = await getApolloClient().mutate({
        mutation: gql`
          mutation GroupTeamMemberRemove($id: UUID!) {
            groupTeamMemberRemove(input: { id: $id }) {
              errors {
                ... on Error {
                  message
                  code
                }
              }
              result {
                id
              }
            }
          }
        `,
        variables: { id: member.id }
      });
      if (data.groupTeamMemberRemove?.result) {
        Toasts.push({
          kind: 'success',
          content: 'Team member removed successfully.'
        });
        dispatch('refetch');
      } else {
        const error = data.groupTeamMemberRemove?.errors?.[0];
        Toasts.push({
          kind: 'error',
          content: error?.message ?? 'There was an issue removing the team member.'
        });
      }
    } catch (error) {
      captureException(error);
    } finally {
      loading = false;
    }
  }

  const IMAGE_PROPS = {
    aspect: 194 / 271,
    fit: 'cover',
    sizes: { sm: 200 },
    src: member.account?.avatar?.original?.url,
    alt: 'Avatar'
  };

  let className = '';
  export { className as class };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="bg-ondark-medium group relative aspect-[194/271] rounded-lg {className}" on:click>
  <!-- Hover Blur -->
  <Image
    class="duration-400 absolute left-0 top-0 size-full scale-75 object-cover blur-xl transition-all will-change-[transform,opacity] {isActive
      ? 'scale-90'
      : 'group-hover:scale-90'}"
    {...IMAGE_PROPS}
  />

  <!-- Blurred Border -->
  <div class="relative size-full overflow-hidden rounded-[inherit]">
    <Image
      class="size-full object-cover blur-xl transition-transform {isActive ? 'scale-125' : 'group-hover:scale-125'}"
      {...IMAGE_PROPS}
    />
  </div>

  <!-- Avatar -->
  <Image class="absolute inset-px size-[calc(100%-2px)] object-cover" rounded="rounded-[inherit]" {...IMAGE_PROPS} />

  {#if member.state === GroupTeamMemberStateEnum.Requested}
    <div
      class="text-alert-ondark-base bg-alert-ondark-subtle absolute left-4 top-0 flex h-6 items-center gap-1.5 rounded-b px-2"
    >
      <IconWrapper icon={faTriangleExclamation} size="sm" />
      <span class="text-body-4xs">Pending</span>
    </div>
  {/if}

  <div
    class="absolute inset-px rounded-[inherit]"
    style="background: linear-gradient(0deg, rgba(14, 14, 17, 0.95) 4.73%, rgba(14, 14, 17, 0.85) 22.69%, rgba(14, 14, 17, 0) 51.4%)"
  ></div>

  <div class="absolute inset-x-0 bottom-0 px-4 py-5 text-center">
    <p class="text-body-lg text-ondark-primary">{member.account.name}</p>
    {#if isOwner}
      <p class="text-body-xs mt-2">Your revenue split: {member.percentageSplit}%</p>
    {:else if game}
      <div class="mt-2 flex items-center justify-center gap-2">
        <Image
          src={game.logoMain?.original?.url}
          sizes={{ sm: 16 }}
          rounded="rounded-full"
          class="size-4"
          alt="Game Logo"
        />
        <span class="text-body-xs text-ondark-primary truncate">
          {game.localizedShortTitles?.en || game.localizedTitles?.en}
        </span>

        {#if member.account.gamesOffered?.totalCount > 1}
          <Tag size="sm" variant="secondary">
            +{member.account.gamesOffered.totalCount - 1}
          </Tag>
        {/if}
      </div>
    {/if}
  </div>

  {#if loading}
    <div class="bg-ondark-overlay absolute left-0 top-0 flex size-full items-center justify-center rounded-[inherit]">
      <LoadingSpinner size="lg" theme="dark" />
    </div>
  {/if}

  {#if link}
    <a href="/@{member.account.slug}" class="absolute inset-0">
      <span class="sr-only">
        View {member.account.name}'s profile
      </span>
    </a>
  {/if}

  {#if isSortable}
    <div transition:scale|local class="mfy-orderable-handle absolute left-2 top-2">
      <IconButton icon={faGripDotsVertical} variant="secondary" size="md" />
    </div>
  {/if}

  {#if isEditable}
    <div class="absolute right-2 top-2">
      <Dropdown placement="bottom-end" size="sm">
        <IconButton slot="trigger" icon={faEllipsis} variant="secondary" size="md" />

        <DropdownMenuItem icon={faPen} label="Edit community fee" on:click={() => (isUpdateTeamMemberOpen = true)} />
        <DropdownMenuDivider />
        <DropdownMenuItem
          icon={faTrash}
          label="Remove"
          on:click={() => handleRemoveTeamMember()}
          iconColor="text-alert-ondark-base"
          variant="danger"
        />
      </Dropdown>
    </div>
  {/if}
</div>

<Suspense condition={isUpdateTeamMemberOpen} promise={AddTeamMemberLazy} let:component={UpdateTeamMember}>
  <UpdateTeamMember bind:isOpen={isUpdateTeamMemberOpen} step="EDIT_REVENUE_SPLIT" selectedMember={member} on:refetch />
</Suspense>
